// tournament/blinds.ts - the blind/ante schedule model and the effective-stack-in-BB arithmetic
// (ST1, BUILD_PLAN §ST1: "blind/ante schedule model … blind progression advances stacks-in-BB
// deterministically"). Pure + deterministic: no clock, no randomness - a schedule is an ordered list
// of levels, and every derived quantity (stack in BB, M-ratio, the per-level progression) is a plain
// function of (chips, level). Nothing here does I/O or loads data.
//
// Scope note (ST1): this models a SINGLE table's blind/ante progression as the game-condition input to
// the strategy layer. The multi-table shrinking-field simulation (busts → table breaks/combines → a
// final table) and named save/resume are the separately-scoped Phase 7 requirements flagged in
// BUILD_PLAN §ST-UI, NOT part of ST1.
/** True iff a schedule is well-formed: ≥1 level, levels numbered 1..n ascending, and blinds + antes
 *  non-decreasing across the structure (they only ever rise). */
export const validateSchedule = (s) => {
    if (s.levels.length === 0)
        return false;
    for (let i = 0; i < s.levels.length; i++) {
        const lv = s.levels[i];
        if (lv.level !== i + 1)
            return false;
        if (lv.sb <= 0 || lv.bb <= 0 || lv.ante < 0)
            return false;
        if (lv.bb < lv.sb)
            return false;
        if (i > 0) {
            const prev = s.levels[i - 1];
            if (lv.bb < prev.bb || lv.sb < prev.sb || lv.ante < prev.ante)
                return false;
        }
    }
    return true;
};
/** Effective stack expressed in big blinds - the single number tournament strategy keys on (staging,
 *  push/fold band, ICM depth). Rounded to 0.1bb so it reads cleanly; the raw ratio is `chips / bb`. */
export const effectiveStackBB = (chips, bb) => {
    if (bb <= 0)
        throw new RangeError(`bb must be positive, got ${bb}`);
    return Math.round((chips / bb) * 10) / 10;
};
/**
 * Harrington M-ratio: how many orbits the stack can survive paying the blinds + the table ante once
 * per hand. `cost per orbit = sb + bb + ante` (the BB ante is one ante-sized chunk posted per hand,
 * which over an orbit is ~one ante at the table, so we fold it into the per-orbit cost alongside the
 * blinds - the standard live approximation for a single BB-ante). Lower M ⇒ deeper into push/fold.
 */
export const mRatio = (chips, level) => {
    const cost = level.sb + level.bb + level.ante;
    if (cost <= 0)
        throw new RangeError(`orbit cost must be positive`);
    return Math.round((chips / cost) * 100) / 100;
};
/** The effective stack in BB at each level for a FIXED chip stack - the deterministic progression the
 *  done-criteria pins: as the blinds rise the same chips are worth strictly fewer BB (until the blinds
 *  stop rising). Returns one entry per schedule level, in level order. */
export const stackProgression = (chips, schedule) => schedule.levels.map((lv) => ({
    level: lv.level,
    sb: lv.sb,
    bb: lv.bb,
    ante: lv.ante,
    stackBB: effectiveStackBB(chips, lv.bb),
    m: mRatio(chips, lv),
}));
/** The level record for a 1-based level number (throws if out of range). */
export const levelAt = (schedule, level) => {
    const lv = schedule.levels[level - 1];
    if (!lv || lv.level !== level)
        throw new RangeError(`no level ${level} in schedule ${schedule.id}`);
    return lv;
};
// ── Default local structures (North Star / Phase 7 scope: local $100 / $200 buy-ins) ──────────────
// Chip counts, not dollars - buy-in is a display label. Both start at 10bb-ish depth per starting
// stack that these local dailies actually use, with big-blind antes kicking in at the middle levels
// (the standard modern local structure). Encoded here so ST2 scenarios and ST-UI presets have a real
// rising structure to progress against; the exact values are common local-daily blind ladders.
/** $100 local daily - 20,000 starting chips, 20-minute levels (clock not modeled). BB antes from L4. */
export const LOCAL_100_SCHEDULE = {
    id: 'local_100',
    label: '$100 Local Daily',
    levels: [
        { level: 1, sb: 100, bb: 100, ante: 0 },
        { level: 2, sb: 100, bb: 200, ante: 0 },
        { level: 3, sb: 200, bb: 300, ante: 0 },
        { level: 4, sb: 200, bb: 400, ante: 400 },
        { level: 5, sb: 300, bb: 500, ante: 500 },
        { level: 6, sb: 400, bb: 600, ante: 600 },
        { level: 7, sb: 500, bb: 800, ante: 800 },
        { level: 8, sb: 500, bb: 1000, ante: 1000 },
        { level: 9, sb: 700, bb: 1500, ante: 1500 },
        { level: 10, sb: 1000, bb: 2000, ante: 2000 },
        { level: 11, sb: 1500, bb: 3000, ante: 3000 },
        { level: 12, sb: 2000, bb: 4000, ante: 4000 },
    ],
};
/** $200 local daily - 30,000 starting chips, deeper/slower top-heavy structure. BB antes from L4. */
export const LOCAL_200_SCHEDULE = {
    id: 'local_200',
    label: '$200 Local Daily',
    levels: [
        { level: 1, sb: 100, bb: 200, ante: 0 },
        { level: 2, sb: 200, bb: 300, ante: 0 },
        { level: 3, sb: 200, bb: 400, ante: 0 },
        { level: 4, sb: 300, bb: 500, ante: 500 },
        { level: 5, sb: 400, bb: 600, ante: 600 },
        { level: 6, sb: 500, bb: 800, ante: 800 },
        { level: 7, sb: 500, bb: 1000, ante: 1000 },
        { level: 8, sb: 700, bb: 1500, ante: 1500 },
        { level: 9, sb: 1000, bb: 2000, ante: 2000 },
        { level: 10, sb: 1500, bb: 3000, ante: 3000 },
        { level: 11, sb: 2000, bb: 4000, ante: 4000 },
        { level: 12, sb: 3000, bb: 5000, ante: 5000 },
    ],
};
/** The default starting chip stacks that pair with the two local schedules (for stackProgression). */
export const LOCAL_100_START_CHIPS = 20000;
export const LOCAL_200_START_CHIPS = 30000;
//# sourceMappingURL=blinds.js.map